import type { StorageContract } from '@shell/contracts/storage.contract'
import { AUTH_KEYS, COGNITO_KEY_PREFIX } from './keys'

/**
 * LocalStorageAdapter — implements StorageContract using browser localStorage.
 *
 * Key improvements over raw localStorage usage:
 * - clearAuth() only removes auth-related keys (replaces localStorage.clear())
 * - Type-safe get<T>() with automatic JSON parse for objects
 * - SSR-safe (no-ops when window is undefined)
 * - Centralized access point for all storage operations
 */
class LocalStorageAdapter implements StorageContract {
  private get isAvailable(): boolean {
    return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
  }

  get<T = string>(key: string): T | null {
    if (!this.isAvailable) return null

    const raw = localStorage.getItem(key)
    if (raw === null) return null

    // Attempt JSON parse for object types
    try {
      return JSON.parse(raw) as T
    } catch {
      // Not JSON — return raw string cast as T
      return raw as unknown as T
    }
  }

  set<T = string>(key: string, value: T): void {
    if (!this.isAvailable) return

    if (typeof value === 'string') {
      localStorage.setItem(key, value)
    } else {
      localStorage.setItem(key, JSON.stringify(value))
    }
  }

  remove(key: string): void {
    if (!this.isAvailable) return
    localStorage.removeItem(key)
  }

  /**
   * Clear only auth-related keys from localStorage.
   *
   * Removes:
   * - Known auth keys (_sfid, appLocale, loadedLocale, lmsLocale, gm)
   * - All Cognito token keys (CognitoIdentityServiceProvider.*)
   *
   * Preserves:
   * - Portal selections (portal-selected-material, etc.)
   * - Feature flags (stored-ff)
   * - UI preferences (autoPlayVideo, activeOPT, etc.)
   * - Learning path state (lp:*, ${sfId}-lpid, etc.)
   *
   * This replaces localStorage.clear() in signOutUser(), which was
   * destroying all stored data including UI preferences.
   */
  clearAuth(): void {
    if (!this.isAvailable) return

    // Remove known auth keys
    const authKeyValues = Object.values(AUTH_KEYS)
    for (const key of authKeyValues) {
      localStorage.removeItem(key)
    }

    // Remove all Cognito-managed token keys
    const keysToRemove: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(COGNITO_KEY_PREFIX)) {
        keysToRemove.push(key)
      }
    }
    for (const key of keysToRemove) {
      localStorage.removeItem(key)
    }
  }

  has(key: string): boolean {
    if (!this.isAvailable) return false
    return localStorage.getItem(key) !== null
  }
}

// ---------------------------------------------------------------------------
// Singleton export
// ---------------------------------------------------------------------------

let instance: StorageContract | null = null

export const getStorageAdapter = (): StorageContract => {
  if (!instance) {
    instance = new LocalStorageAdapter()
  }
  return instance
}
